import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Input, Spin, Tag, Tooltip } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  ReadOutlined,
} from '@ant-design/icons';

import {
  errorMessage,
  fetchNewsCuration,
  resolveTag,
  updateNewsCuration,
} from './api';
import {
  NewsArticle,
  NewsCuration,
  NewsTag,
  TagChoice,
  NewsSource,
} from './types';
import RankingHint from './RankingHint';
import { readable, shortDate } from './format';
import {
  canonicalChoice,
  groupNewsTags,
  NewsTagGroup,
  sameTag,
  tagLabel,
} from './tags';

type Props = {
  item: NewsArticle;
  index: number;
  brush: TagChoice | null;
  onRead: (item: NewsArticle) => void;
  onTagResolved: (tag: NewsTag) => void;
  onChanged: (item: NewsCuration) => void;
  onEditing: (editing: boolean) => void;
  locked?: boolean;
  reviewSelected?: boolean;
  onReviewToggle?: (item: NewsArticle) => void;
  source?: NewsSource;
};

const NewsRow: React.FC<Props> = ({
  item,
  index,
  brush,
  onRead,
  onTagResolved,
  onChanged,
  onEditing,
  locked,
  reviewSelected,
  onReviewToggle,
  source = 'pm',
}) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState<NewsCuration | null>(null);
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [latest, setLatest] = useState<NewsCuration | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const request = useRef<AbortController | null>(null);
  const applied = Boolean(brush && item.tags?.some(tag => sameTag(brush, tag)));
  const tagGroups = groupNewsTags(item.tags || []);
  const brushLabel = brush ? tagLabel(brush) : '';

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
    };
  }, []);

  const edit = async () => {
    if (busy.current || locked) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setEditing(true);
    onEditing(true);
    setLoading(true);
    setError('');
    setConflict(false);
    setLatest(null);
    try {
      const data = await fetchNewsCuration(item.id, controller.signal);
      if (controller.signal.aborted) return;
      setSnapshot(data);
      setHeadline(data.headline);
      setBody(data.body);
    } catch (reason) {
      if (!controller.signal.aborted)
        setError(
          errorMessage(reason) || 'The full article could not be loaded.',
        );
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const cancel = () => {
    request.current?.abort();
    setEditing(false);
    onEditing(false);
    setSnapshot(null);
    setError('');
    setConflict(false);
    setLatest(null);
  };

  const mutate = async (data: Parameters<typeof updateNewsCuration>[1]) => {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      const changed = await updateNewsCuration(item.id, data);
      // A completed mutation must invalidate caches even if the reviewer navigated away.
      onChanged(changed);
      if (mounted.current) {
        setSnapshot(changed);
        setEditing(false);
        onEditing(false);
        setConflict(false);
        setLatest(null);
      }
    } catch (reason) {
      if (mounted.current) {
        setConflict((reason as { status?: number })?.status === 409);
        setError(errorMessage(reason) || 'This change could not be saved.');
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const tag = async (remove?: NewsTagGroup) => {
    if (
      (!brush && !remove) ||
      busy.current ||
      editing ||
      locked ||
      (!remove && applied)
    )
      return;
    busy.current = true;
    setSaving(true);
    setError('');
    try {
      // Refresh the revision for every tagging action; never overwrite a text draft.
      const data = await fetchNewsCuration(item.id);
      if (item.curation_revision && item.curation_revision !== data.revision) {
        onChanged(data);
        if (mounted.current) {
          setConflict(false);
          setError(
            'This article changed. Review the updated text before tagging.',
          );
        }
        return;
      }
      let tagChanges:
        | { remove_tag_ids: number[] }
        | { add_tag_ids: number[] }
        | null = null;
      if (remove) {
        // Remove the entire displayed instrument group atomically, without
        // guessing which physical IDs are safe to discard from a frozen feed.
        const removedIds = remove.tagIds.filter(id =>
          data.tags.some(value => value.id === id),
        );
        if (removedIds.length) tagChanges = { remove_tag_ids: removedIds };
      } else {
        const resolved = canonicalChoice(
          brush!.id
            ? (brush as NewsTag)
            : await resolveTag(brush!.text, brush!.instrument_id),
        );
        onTagResolved(resolved);
        if (!data.tags.some(value => sameTag(resolved, value)))
          tagChanges = { add_tag_ids: [resolved.id] };
      }
      if (tagChanges) {
        onChanged(
          await updateNewsCuration(item.id, {
            expected_revision: data.revision,
            ...tagChanges,
          }),
        );
      } else onChanged(data);
      if (mounted.current) setConflict(false);
    } catch (reason) {
      if (mounted.current) {
        setConflict((reason as { status?: number })?.status === 409);
        setError(errorMessage(reason) || 'The tag could not be saved.');
      }
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const refreshConflict = async () => {
    setRefreshing(true);
    try {
      const data = await fetchNewsCuration(item.id);
      if (mounted.current) setLatest(data);
    } catch (reason) {
      if (mounted.current) setError(errorMessage(reason));
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  };

  return (
    <li
      className={`market-explorer__news-row ${brush && !editing ? 'is-taggable' : ''} ${applied ? 'is-tagged' : ''}`}
    >
      <div className="market-explorer__news-row-heading">
        <button
          type="button"
          className="market-explorer__news-button"
          disabled={saving || editing || (Boolean(brush) && locked)}
          aria-label={
            brush
              ? `${applied ? 'Tagged' : 'Apply'} ${brushLabel} to ${item.headline}`
              : item.headline
          }
          aria-describedby={
            item.ranking || typeof item.score === 'number'
              ? `news-ranking-${item.id}`
              : undefined
          }
          onClick={() => (brush ? void tag() : onRead(item))}
        >
          <span className="market-explorer__news-index">
            {saving ? (
              <Spin size="small" />
            ) : applied ? (
              <CheckOutlined />
            ) : (
              index + 1
            )}
          </span>
          <span className="market-explorer__news-copy">
            <span className="market-explorer__news-meta">
              {item.timestamp
                ? shortDate(item.timestamp)
                : 'Publication date unavailable'}
              {item.timing ? ` · ${readable(item.timing)}` : ''}{' '}
              <RankingHint
                id={`news-ranking-${item.id}`}
                ranking={item.ranking}
                score={item.score}
                reason={item.relevance_reason}
                source={source}
                focusable={false}
              />
            </span>
            <strong>
              {item.source === 'ai_curated' && (
                <Tag color="purple" className="market-explorer__curated-badge">
                  AI Curated
                </Tag>
              )}
              {item.headline}
            </strong>
            {item.source === 'ai_curated' && item.original_headline ? (
              <small className="market-explorer__news-original">
                Original: {item.original_headline}
              </small>
            ) : null}
            {item.cluster_size && item.cluster_size > 1 ? (
              <small>{item.cluster_size} related reports grouped</small>
            ) : null}
          </span>
        </button>
        <div
          className="market-explorer__news-actions"
          role="group"
          aria-label={`Actions for ${item.headline}`}
        >
          {onReviewToggle && (
            <Tooltip title="Include this article when you save or update a review zone. This does not change its global tags.">
              <Checkbox
                className="market-explorer__keep-news"
                checked={reviewSelected}
                disabled={editing || saving || locked}
                onChange={() => onReviewToggle(item)}
                aria-label={`Keep ${item.headline} for zone`}
              >
                Keep for zone
              </Checkbox>
            </Tooltip>
          )}
          <span className="market-explorer__article-actions">
            <Tooltip title="Read the available article text">
              <Button
                type="text"
                size="small"
                icon={<ReadOutlined />}
                aria-label={`Read ${item.headline}`}
                onClick={() => onRead(item)}
              >
                Read
              </Button>
            </Tooltip>
            {!editing && (
              <Tooltip title="Edit the stored headline and full article text">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label={`Edit ${item.headline}`}
                  onClick={() => void edit()}
                  disabled={saving || locked}
                >
                  Edit
                </Button>
              </Tooltip>
            )}
          </span>
        </div>
      </div>
      {Boolean(item.tags?.length) && (
        <div
          className="market-explorer__article-tags"
          aria-label={`Tags for ${item.headline}`}
        >
          {tagGroups.map(group => (
            <Tooltip
              key={group.key}
              title={
                group.tag.instrument_id != null
                  ? `Instrument #${group.tag.instrument_id}${group.tagIds.length > 1 ? ` · ${group.tagIds.length} saved aliases. Removing this chip removes their memberships from this article together.` : ''}`
                  : undefined
              }
            >
              <Tag
                closeIcon={
                  <button
                    type="button"
                    className="market-explorer__tag-remove"
                    aria-label={`Remove ${group.label}`}
                    disabled={saving || editing || locked}
                  >
                    <CloseOutlined aria-hidden="true" />
                  </button>
                }
                onClose={event => {
                  event.preventDefault();
                  void tag(group);
                }}
              >
                <span>{group.label}</span>
              </Tag>
            </Tooltip>
          ))}
        </div>
      )}
      {editing && (
        <div
          className="market-explorer__news-editor"
          aria-label={`Edit article ${item.id}`}
        >
          {loading ? (
            <span>
              <Spin size="small" /> Loading full article…
            </span>
          ) : (
            snapshot && (
              <>
                <label htmlFor={`news-headline-${item.id}`}>Headline</label>
                <Input.TextArea
                  id={`news-headline-${item.id}`}
                  value={headline}
                  onChange={event => setHeadline(event.target.value)}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={saving}
                />
                <label htmlFor={`news-body-${item.id}`}>Article text</label>
                <Input.TextArea
                  id={`news-body-${item.id}`}
                  value={body}
                  onChange={event => setBody(event.target.value)}
                  autoSize={{ minRows: 5, maxRows: 16 }}
                  disabled={saving}
                />
                <p className="market-explorer__muted">
                  Changes are saved to this news article across PM. Source
                  details and the publication date are preserved.
                  {item.cluster_size && item.cluster_size > 1
                    ? ' Only this displayed article is changed; grouped reports keep their own text and tags.'
                    : ''}
                </p>
              </>
            )
          )}
          <div className="market-explorer__editor-actions">
            {snapshot && !loading && (
              <Button
                type="primary"
                size="small"
                loading={saving}
                disabled={
                  !headline.trim() ||
                  conflict ||
                  (headline === snapshot.headline && body === snapshot.body)
                }
                onClick={() =>
                  void mutate({
                    expected_revision: snapshot.revision,
                    headline,
                    body,
                  })
                }
              >
                Save article
              </Button>
            )}
            {!snapshot && !loading && (
              <Button size="small" onClick={() => void edit()}>
                Retry loading article
              </Button>
            )}
            <Button size="small" onClick={cancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {error && (
        <Alert
          type="warning"
          showIcon
          title={
            conflict
              ? 'This article changed while you were reviewing it'
              : 'Change not saved'
          }
          description={
            <>
              <p>{error}</p>
              {conflict && editing && (
                <>
                  <p>
                    Your draft is kept. Review the latest saved version before
                    retrying.
                  </p>
                  <Button
                    size="small"
                    loading={refreshing}
                    onClick={() => void refreshConflict()}
                  >
                    Review latest version
                  </Button>
                </>
              )}
              {conflict && !editing && (
                <p>Click the row again to retry using the latest article.</p>
              )}
            </>
          }
        />
      )}
      {latest && editing && (
        <div className="market-explorer__conflict-review">
          <h4>Latest saved version</h4>
          <strong>{latest.headline}</strong>
          <pre>{latest.body}</pre>
          <Button
            size="small"
            onClick={() => {
              setSnapshot(latest);
              setLatest(null);
              setConflict(false);
              setError('');
            }}
          >
            Keep my draft and retry
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSnapshot(latest);
              setHeadline(latest.headline);
              setBody(latest.body);
              setLatest(null);
              setConflict(false);
              setError('');
            }}
          >
            Use latest version
          </Button>
        </div>
      )}
    </li>
  );
};

export default NewsRow;
